import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Lightbulb, ArrowLeft, Sparkles, Save, X, Package, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { SearchableSelect } from "@/lib/picker-search";
import { useToast } from "@/hooks/use-toast";
import type { Product, Offer, Narrative, IdeationRun } from "@shared/schema";
import { useState } from "react";
import { Link } from "wouter";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  briefDescription: z.string().min(10, "Brief must be at least 10 characters"),
  productId: z.string().optional(),
  offerId: z.string().optional(),
  narrativeId: z.string().optional(),
  category: z.string().optional(),
  sourceNotes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export default function IdeatorCreatePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [customerLanguage, setCustomerLanguage] = useState<string[]>([]);
  const [languageInput, setLanguageInput] = useState("");
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const isOfferMode = promotedItemType === "offer";

  const { data: products } = useQuery<Product[]>({
    queryKey: ["/api/brands", brandId, "products"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  const { data: offers } = useQuery<Offer[]>({
    queryKey: ["/api/brands", brandId, "offers"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  const { data: narratives } = useQuery<Narrative[]>({
    queryKey: ["/api/brands", brandId, "narratives"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      briefDescription: "",
      productId: "",
      offerId: "",
      narrativeId: "",
      category: "",
      sourceNotes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: FormValues & { generateNow?: boolean }) => {
      const { generateNow, productId, offerId, narrativeId, ...rest } = values;
      const body: any = {
        ...rest,
        ...(promotedItemType === "product" && productId ? { productId } : {}),
        ...(promotedItemType === "offer" && offerId ? { offerId } : {}),
        ...(narrativeId ? { narrativeId } : {}),
        sourceCustomerLanguageJson: customerLanguage.length > 0 ? customerLanguage : undefined,
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/ideation-runs`, body);
      const run: IdeationRun = await res.json();
      if (generateNow) {
        await apiRequest("POST", `/api/ideation-runs/${run.id}/generate`);
      }
      return run;
    },
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "ideation-runs"] });
      toast({ title: "Ideation run created" });
      navigate(`/brands/${brandId}/ideator/${run.id}`);
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const handleAddLanguage = () => {
    const trimmed = languageInput.trim();
    if (trimmed && !customerLanguage.includes(trimmed)) {
      setCustomerLanguage([...customerLanguage, trimmed]);
      setLanguageInput("");
    }
  };

  const handleRemoveLanguage = (phrase: string) => {
    setCustomerLanguage(customerLanguage.filter((p) => p !== phrase));
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link href={`/brands/${brandId}/ideator`}>
          <Button variant="ghost" size="icon" className="rounded-full" data-testid="button-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Lightbulb className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">New Ideation Run</h1>
            <p className="text-sm text-muted-foreground">Describe your market hypothesis to generate candidate personas</p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form className="space-y-6">
          <div className="rounded-xl border bg-card p-6 space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Run Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Q2 Skincare Persona Discovery" {...field} data-testid="input-run-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="briefDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Strategic Brief</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the market segment, customer signals, or hypothesis you want to explore. Be specific about the type of buyer you're trying to understand..."
                      className="min-h-[120px] resize-y"
                      {...field}
                      data-testid="input-brief"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
                    {isOfferMode ? "Offer (optional)" : "Product (optional)"}
                  </span>
                  <div
                    className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60"
                    data-testid="toggle-promoted-item"
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant={isOfferMode ? "ghost" : "default"}
                      className="h-6 px-2.5 text-[11px] gap-1 rounded-full"
                      onClick={() => setPromotedItemType("product")}
                      data-testid="toggle-promoted-product"
                    >
                      <Package className="h-3 w-3" /> Product
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={isOfferMode ? "default" : "ghost"}
                      className="h-6 px-2.5 text-[11px] gap-1 rounded-full"
                      onClick={() => setPromotedItemType("offer")}
                      data-testid="toggle-promoted-offer"
                    >
                      <Gift className="h-3 w-3" /> Offer
                    </Button>
                  </div>
                </div>
                {isOfferMode ? (
                  <FormField
                    control={form.control}
                    name="offerId"
                    render={({ field }) => (
                      <FormItem>
                        <SearchableSelect
                          value={field.value ?? ""}
                          onValueChange={field.onChange}
                          placeholder="Select offer"
                          testId="select-offer"
                          options={(offers ?? []).map((o) => ({ value: o.id, label: o.name }))}
                        />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="productId"
                    render={({ field }) => (
                      <FormItem>
                        <SearchableSelect
                          value={field.value ?? ""}
                          onValueChange={field.onChange}
                          placeholder="Select product"
                          testId="select-product"
                          options={(products ?? []).map((p) => ({ value: p.id, label: p.name }))}
                        />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <FormField
                control={form.control}
                name="narrativeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Narrative (optional)</FormLabel>
                    <SearchableSelect
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      placeholder="Select narrative"
                      testId="select-narrative"
                      options={(narratives ?? []).map((n) => ({ value: n.id, label: n.name }))}
                    />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Category (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Health & Wellness, DTC Beauty" {...field} data-testid="input-category" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sourceNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Source Notes (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Customer interviews, survey data, review mining insights, competitor analysis..."
                      className="min-h-[80px] resize-y"
                      {...field}
                      data-testid="input-source-notes"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div>
              <label className="text-xs uppercase tracking-wider font-medium text-muted-foreground block mb-2">
                Customer Language Phrases (optional)
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a phrase customers use..."
                  value={languageInput}
                  onChange={(e) => setLanguageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddLanguage();
                    }
                  }}
                  data-testid="input-language-phrase"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddLanguage} data-testid="button-add-phrase">
                  Add
                </Button>
              </div>
              {customerLanguage.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {customerLanguage.map((phrase) => (
                    <Badge key={phrase} variant="secondary" className="text-xs gap-1">
                      {phrase}
                      <button type="button" onClick={() => handleRemoveLanguage(phrase)} data-testid={`button-remove-phrase-${phrase}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              disabled={createMutation.isPending}
              onClick={() => {
                const values = form.getValues();
                const result = formSchema.safeParse(values);
                if (!result.success) {
                  form.trigger();
                  return;
                }
                createMutation.mutate({ ...values, generateNow: false });
              }}
              data-testid="button-save-draft"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            <Button
              type="button"
              className="rounded-full"
              disabled={createMutation.isPending}
              onClick={() => {
                const values = form.getValues();
                const result = formSchema.safeParse(values);
                if (!result.success) {
                  form.trigger();
                  return;
                }
                createMutation.mutate({ ...values, generateNow: true });
              }}
              data-testid="button-generate"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {createMutation.isPending ? "Generating..." : "Generate Candidate Personas"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
